import {
  getOnBoardPassengers,
  getPassengerCount,
} from '@backoffice-frontend/ad-passenger-list'
import { ContentInfo, MoiaButton } from '@backoffice-frontend/patterns'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import { HeadsetIcon } from '@moia-dev/pace-icons'
import { Trans, useTranslation } from 'react-i18next'
import { NotImplemented } from '../../NotImplemented'
import { SecondaryActionFlowButton } from '../../SecondaryActionFlowButton'
import { SpeechBubble } from '../../SpeechBubble'
import { getSeatingCounts } from '../../seating'
import { TelematicsCheck } from '../../TelematicsCheck'
import { ActionCard } from '../ActionCard'
import type { AdVehicleStreamPassengerUnbuckledActionCardVehicleFragment } from './PassengerUnbuckledActionCard.hook'

export const PassengerUnbuckledActionCard = ({
  vehicle,
  isAlertSolved,
  onClose,
}: {
  vehicle: AdVehicleStreamPassengerUnbuckledActionCardVehicleFragment
  isAlertSolved: boolean
  onClose: VoidFunction
}) => {
  const { t } = useTranslation()
  const { user } = useBackofficeLoggedInUser()

  const onBoardPassengers = getOnBoardPassengers(
    vehicle.fccUpcomingWaypoints ?? [],
  )
  const expectedPassengerCount = getPassengerCount(onBoardPassengers)
  const { buckledUp } = getSeatingCounts(vehicle)
  const isAllPassengersBuckled = buckledUp >= expectedPassengerCount

  return (
    <ActionCard
      headline={t('adFleetMap_passengerUnbuckled_label')}
      instructions={t('adFleetMap_unbuckledSubline_caption')}
    >
      <div
        css={theme => ({
          display: 'flex',
          flexDirection: 'column',
          padding: theme.spacing(4, 3, 6, 3),
          height: '100%',
          justifyContent: 'space-between',
        })}
      >
        <div
          css={theme => ({
            display: 'flex',
            gap: theme.spacing(1),
            alignSelf: 'center',
          })}
        >
          <div
            css={theme => ({
              border: `1px solid ${theme.semantic.ColorBorderOnActionSecondaryDefault}`,
              background: theme.semantic.ColorAccessoryReducedAlternative,
              borderRadius: '100%',
              height: 40,
              width: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'flex-end',
            })}
          >
            <HeadsetIcon
              css={theme => ({ color: theme.semantic.ColorContentReduced })}
            />
          </div>
          <SpeechBubble>
            <Trans components={[<strong />]}>
              {t('adFleetMap_operatorScriptHello_caption', {
                operatorName: user?.firstName,
              })}
            </Trans>
            <br />
            {t('adFleetMap_unbuckledSpeechBubbleWeNoticed_caption')}
            <br />
            {t('adFleetMap_pleaseBuckleUpAgain_label')}
            <br />
            <strong>{t('adFleetMap_passenger_offerHelp_label')}</strong>
          </SpeechBubble>
        </div>

        <div>
          <div
            css={theme => ({
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(3),
            })}
          >
            <TelematicsCheck
              title={t('adFleetMap_cscAllPassengersBuckledUp_caption')}
              variant={isAllPassengersBuckled ? 'success' : 'pending'}
            />
            <TelematicsCheck
              title={
                isAlertSolved
                  ? t('adFleetMap_incidentResolved_label')
                  : t('adFleetMap_incidentUnresolved_label')
              }
              subtitle={false}
              variant={isAlertSolved ? 'success' : 'pending'}
            />
          </div>
        </div>

        <div
          css={theme => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(3),
          })}
        >
          <NotImplemented>
            <SecondaryActionFlowButton>
              {t('adFleetMap_passengerWantsToLeave_label')}
            </SecondaryActionFlowButton>
          </NotImplemented>
          <NotImplemented>
            <SecondaryActionFlowButton>
              {t('adFleetMap_passengerDeclinesToBuckleUp_label')}
            </SecondaryActionFlowButton>
          </NotImplemented>
        </div>

        {isAlertSolved ? (
          <MoiaButton
            onClick={onClose}
            color="primary"
            css={{ alignSelf: 'center' }}
          >
            {t('adFleetMap_tripCanContinue_button')}
          </MoiaButton>
        ) : (
          <ContentInfo
            title={t('adFleetMap_tripContinueConditions_caption')}
            css={{ width: 360, alignSelf: 'center' }}
          />
        )}
      </div>
    </ActionCard>
  )
}
